import { useMemo, useState, type FormEvent } from "react";
import { useAsync } from "../useAsync.js";
import {
  automaticSettlementConfirmation,
  compatibleLessonBlockProducts,
  lessonBlockRegistrationInput,
  lessonBlockSaleAttempt,
  normalizePaymentAmount,
  type AutomaticSettlementPolicy,
  type LessonBlockRegistrationInput,
} from "./lessonBlockSaleModel.js";
import type { PaymentOperationAttempt } from "./paymentOperationModel.js";
import {
  staffBooking,
  type LessonBlockDetail,
  type LessonBlockProduct,
  type OnSitePaymentMethod,
  type ProviderService,
} from "./staff.js";
import { notifyStudentCreditStateChanged } from "./studentCreditEvents.js";
import { AcceptedRegistrationRecovery } from "./AcceptedRegistrationRecovery.js";
import { useLessonBlockPaymentAmount } from "./useLessonBlockPaymentAmount.js";
import { AutomaticSettlementPolicyChoice } from "./AutomaticSettlementPolicyChoice.js";

const PAYMENT_METHODS: { value: OnSitePaymentMethod; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "card_terminal", label: "Card machine" },
  { value: "bank_transfer", label: "Bank transfer" },
];

const errorMessage = (cause: unknown, fallback: string) =>
  cause instanceof Error ? cause.message : fallback;

export function TutorAddCredits({
  studentId,
  studentName,
  ownProviderId,
  initialServiceName,
  products,
  onCancel,
  onApply,
}: {
  studentId: string;
  studentName: string;
  ownProviderId: number;
  initialServiceName?: string | null;
  products: LessonBlockProduct[];
  onCancel: () => void;
  onApply: (mutation: () => Promise<LessonBlockDetail>) => Promise<void>;
}) {
  const {
    data: services,
    error: serviceError,
    loading: servicesLoading,
  } = useAsync<ProviderService[]>(
    () => staffBooking.providerServices(ownProviderId),
    [ownProviderId],
  );
  const serviceById = useMemo(
    () => new Map((services ?? []).map((service) => [service.id, service])),
    [services],
  );
  const compatible = useMemo(
    () => compatibleLessonBlockProducts(products, services ?? [], 5),
    [products, services],
  );
  const [productId, setProductId] = useState("");
  const product = compatible.find((item) => item.id === productId) ??
    compatible.find((item) => serviceById.get(item.amelia_lesson_service_id)?.name === initialServiceName) ?? compatible[0];
  const service = product ? serviceById.get(product.amelia_lesson_service_id) : undefined;
  const serviceName = service?.name ?? product?.display_name ?? "lesson";
  const [amount, setAmount] = useLessonBlockPaymentAmount(product, services ?? []);
  const [method, setMethod] = useState<OnSitePaymentMethod>("cash");
  const [automaticSettlementPolicy, setAutomaticSettlementPolicy] =
    useState<AutomaticSettlementPolicy>("settle_outstanding_first");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<PaymentOperationAttempt | null>(null);
  // A registration the server accepted, frozen for replay-only recovery.
  const [accepted, setAccepted] = useState<{
    input: LessonBlockRegistrationInput;
    key: string;
    message: string;
  } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!product || busy) return;

    const paymentAmount = normalizePaymentAmount(amount);
    if (!paymentAmount) {
      setProblem("Enter the amount that was received, with up to two decimal places.");
      return;
    }

    const input = lessonBlockRegistrationInput({
      studentId,
      studentName,
      providerId: ownProviderId,
      product,
      paymentAmount,
      paymentMethod: method,
      automaticSettlementPolicy,
    });
    const nextAttempt = lessonBlockSaleAttempt(attempt, input);
    setAttempt(nextAttempt);
    await register(input, nextAttempt.key);
  }

  async function register(input: LessonBlockRegistrationInput, key: string) {
    setBusy(true);
    setProblem(null);
    // Set the moment the server returns the block, before any follow-up read
    // or settlement check can fail.
    let registered = false;
    try {
      await onApply(async () => {
        const detail = await staffBooking.registerLessonBlock(input, key);
        registered = true;
        notifyStudentCreditStateChanged(studentId);
        return detail;
      });
    } catch (cause: unknown) {
      const message = errorMessage(
        cause,
        "The credits could not be confirmed. Retry this same addition before starting another.",
      );
      if (registered) {
        // The block exists, whether settlement or the authoritative re-read
        // failed. Freeze exactly what was accepted so later edits can never
        // mint a new key and register a second block.
        setAccepted({ input, key, message });
        return;
      }
      // Before acceptance (timeout, 503), keep the attempt: retrying the
      // unchanged form replays the same logical registration. A failed replay
      // from the locked view stays locked on the accepted request.
      setProblem(message);
    } finally {
      setBusy(false);
    }
  }

  if (accepted) {
    return (
      <AcceptedRegistrationRecovery
        title="Add 5 credits"
        message={accepted.message}
        problem={problem}
        busy={busy}
        onRetry={() => void register(accepted.input, accepted.key)}
        onClose={onCancel}
      />
    );
  }

  return (
    <form style={{ marginTop: 12 }} onSubmit={submit}>
      <h3>Add 5 credits</h3>
      <p className="small muted">
        Record a normal five-lesson block that this student has already paid for outside the Hub.
        The Hub does not take or process payments.
      </p>
      <p className="small muted">
        If the block was bought in Amelia and has not appeared here yet, wait for the importer rather
        than adding it again.
      </p>
      {problem && <div className="error small">{problem}</div>}
      {serviceError && <div className="error small">{serviceError}</div>}
      {servicesLoading && <p className="empty small">Checking your configured lesson services…</p>}
      {!servicesLoading && !serviceError && compatible.length === 0 && (
        <div className="notice small">
          No configured 5-credit block is available for any of your private lesson services.
        </div>
      )}

      {product && (
        <>
          {compatible.length > 1 ? (
            <label>
              <span className="small muted">Lesson service</span>
              <select
                value={product.id}
                onChange={(event) => setProductId(event.target.value)}
                required
              >
                {compatible.map((item) => (
                  <option value={item.id} key={item.id}>
                    {serviceById.get(item.amelia_lesson_service_id)?.name ?? item.display_name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="small">
              <span className="muted">Lesson service: </span>
              <strong>{serviceName}</strong>
            </p>
          )}

          <div className="fields" style={{ marginTop: 10 }}>
            <label>
              <span className="small muted">Payment already received by</span>
              <select
                value={method}
                onChange={(event) => setMethod(event.target.value as OnSitePaymentMethod)}
                required
              >
                {PAYMENT_METHODS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="small muted">Amount received</span>
              <input
                inputMode="decimal"
                pattern="[0-9]+([.][0-9]{1,2})?"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                required
              />
            </label>
          </div>

          <AutomaticSettlementPolicyChoice
            name="tutor-credit-allocation"
            value={automaticSettlementPolicy}
            onChange={(value) => {
              setAutomaticSettlementPolicy(value);
              setAttempt(null);
              setProblem(null);
            }}
            disabled={busy}
          />

          <div className="notice small" style={{ marginTop: 12 }}>
            <strong>Add 5 {serviceName} credits to {studentName}?</strong>
            <div>
              {automaticSettlementConfirmation(automaticSettlementPolicy, 5)} This records a payment
              already received elsewhere; it does not take a payment or write a new block to Amelia.
            </div>
          </div>
        </>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button type="submit" disabled={busy || !product}>
          {busy ? "Adding…" : `Add 5 ${serviceName} credits · £${amount} ${PAYMENT_METHODS.find((item) => item.value === method)?.label ?? method}`}
        </button>
        <button type="button" className="quiet" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
